import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart, Bar, LineChart, Line, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import api           from '../../api/client';
import { useAuth }  from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import ExportButtons from '../../components/ExportButtons';
import HeatmapChart  from '../../components/HeatmapChart';

/* ═══ Tarih yardımcıları — UTC kayması yok ════════════════════════════════ */
const TZ       = 'Europe/Istanbul';
const todayStr = () => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());
const daysAgo  = n => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(d);
};
const safeDate = raw => {
  if (!raw) return '';
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-${String(d.getUTCDate()).padStart(2,'0')}`;
};
const fmtS = d => { const s = safeDate(d); return s ? `${s.slice(8,10)}.${s.slice(5,7)}` : ''; };
const fmtL = d => { const s = safeDate(d); return s ? `${s.slice(8,10)}.${s.slice(5,7)}.${s.slice(0,4)}` : ''; };
const fmt  = n => Number(n || 0).toLocaleString('tr-TR');

const HOUR_OPTS  = Array.from({ length: 24 }, (_, h) => ({ v: h, l: `${String(h).padStart(2,'0')}:00` }));
const TREND_CFGS = [
  { key:'daily',   label:'Günlük',   ep:'/reports/daily',   p:{ days:30   } },
  { key:'weekly',  label:'Haftalık', ep:'/reports/weekly',  p:{ weeks:12  } },
  { key:'monthly', label:'Aylık',    ep:'/reports/monthly', p:{ months:12 } },
  { key:'yearly',  label:'Yıllık',   ep:'/reports/yearly',  p:{}            },
];

/* ═══ Modül menü tanımları ════════════════════════════════════════════════ */
const STORE_MODULES = [
  { id:'perf',      icon:'ti-building-store',  label:'Şube Performansı'          },
  { id:'cmpdev',    icon:'ti-scale',           label:'Şubeler Arası Karşılaştırma'},
  { id:'trend',     icon:'ti-chart-line',      label:'Periyodik Trend'            },
  { id:'range',     icon:'ti-calendar-stats',  label:'Tarih Aralığı Özeti'        },
  { id:'hourrange', icon:'ti-clock',           label:'Saat Aralığı Raporu'        },
  { id:'cmpdate',   icon:'ti-arrows-diff',     label:'Tarih Karşılaştırması'      },
  { id:'topdays',   icon:'ti-trophy',          label:'En Yoğun Günler'            },
  { id:'hours',     icon:'ti-clock-hour-4',    label:'Saat Analizi'               },
  { id:'heatmap',   icon:'ti-grid-pattern',    label:'Isı Haritası'               },
  { id:'export',    icon:'ti-download',        label:'Dışa Aktar'                 },
];
const MALL_MODULES = [
  { id:'trend',     icon:'ti-chart-line',      label:'Periyodik Trend'            },
  { id:'perf',      icon:'ti-door-enter',      label:'Kapı Performansı'           },
  { id:'cmpdev',    icon:'ti-scale',           label:'Kapı Karşılaştırması'       },
  { id:'range',     icon:'ti-calendar-stats',  label:'Tarih Aralığı Özeti'        },
  { id:'hourrange', icon:'ti-clock',           label:'Saat Aralığı Raporu'        },
  { id:'cmpdate',   icon:'ti-arrows-diff',     label:'Tarih Karşılaştırması'      },
  { id:'topdays',   icon:'ti-trophy',          label:'En Yoğun Günler'            },
  { id:'hours',     icon:'ti-clock-hour-4',    label:'Saat Analizi'               },
  { id:'heatmap',   icon:'ti-grid-pattern',    label:'Isı Haritası'               },
  { id:'export',    icon:'ti-download',        label:'Dışa Aktar'                 },
];

/* ═══ Küçük paylaşılan bileşenler ════════════════════════════════════════ */
const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border2)', borderRadius:8, padding:'10px 14px', fontSize:12 }}>
      <div style={{ color:'var(--text2)', marginBottom:4 }}>{label}</div>
      {payload.map(p => <div key={p.dataKey} style={{ color:p.color }}>{p.name}: <strong>{fmt(p.value)}</strong></div>)}
    </div>
  );
};

const KCard = ({ label, value, color='var(--text)', sub, accent, loading }) => (
  <div className="card" style={{ padding:'13px 15px', minWidth:0, borderTop: accent ? `2px solid ${accent}` : undefined, borderRadius: accent ? '0 0 var(--r) var(--r)' : undefined }}>
    <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'.06em', color:'var(--text3)', marginBottom:5 }}>{label}</div>
    {loading
      ? <div style={{ height:26, background:'var(--border)', borderRadius:4, animation:'pulse 1.2s ease infinite' }} />
      : <div style={{ fontSize:22, fontWeight:700, fontFamily:'var(--font-display)', color, lineHeight:1 }}>{value ?? '—'}</div>}
    {sub && <div style={{ fontSize:10, color:'var(--text3)', marginTop:3 }}>{sub}</div>}
  </div>
);

const KRow = ({ cols = 4, children }) => (
  <div style={{ display:'grid', gridTemplateColumns:`repeat(${cols}, minmax(0,1fr))`, gap:10, marginBottom:16 }}>
    {children}
  </div>
);

const Spn = () => <div style={{ padding:40, textAlign:'center' }}><span className="spinner" /></div>;
const Mty = ({ msg='Veri yok' }) => (
  <div style={{ height:80, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text3)', fontSize:13 }}>{msg}</div>
);

const DatePair = ({ start, end, onStart, onEnd, label }) => (
  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
    {label && <span style={{ fontSize:12, fontWeight:600, color:'var(--text2)' }}>{label}</span>}
    <input type="date" value={start} onChange={e => onStart(e.target.value)} style={{ width:145 }} />
    <span style={{ color:'var(--text3)' }}>—</span>
    <input type="date" value={end}   onChange={e => onEnd(e.target.value)}   style={{ width:145 }} />
  </div>
);

const HourPair = ({ sh, eh, onSh, onEh }) => (
  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
    <span style={{ fontSize:12, fontWeight:600, color:'var(--text2)' }}>Saat:</span>
    <select value={sh} onChange={e => onSh(Number(e.target.value))} style={{ width:90 }}>
      {HOUR_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
    <span style={{ color:'var(--text3)' }}>—</span>
    <select value={eh} onChange={e => onEh(Number(e.target.value))} style={{ width:90 }}>
      {HOUR_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </div>
);

const ApplyBtn = ({ onClick, loading, disabled }) => (
  <button className="btn btn-primary btn-sm" onClick={onClick} disabled={loading || disabled}>
    {loading ? <><span className="spinner" style={{ width:12, height:12 }} /> Yükleniyor</> : 'Uygula'}
  </button>
);

const QuickDates = ({ onSelect }) => (
  <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
    {[['Son 7 gün',6],['Son 30 gün',29],['Son 90 gün',89]].map(([l,n]) => (
      <button key={l} className="btn btn-secondary btn-sm" onClick={() => onSelect(daysAgo(n), todayStr())}>{l}</button>
    ))}
  </div>
);

const TrendSubtabs = ({ tab, onTab, loading, onRefresh }) => (
  <div style={{ display:'flex', gap:2, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r-sm)', padding:3, width:'fit-content', marginBottom:14 }}>
    {TREND_CFGS.map(t => (
      <button key={t.key} onClick={() => onTab(t.key)}
        style={{ padding:'5px 16px', borderRadius:5, border:'none', fontSize:13, fontWeight:500, cursor:'pointer',
          background: tab===t.key ? 'var(--accent)' : 'transparent',
          color:      tab===t.key ? '#fff' : 'var(--text2)' }}>
        {t.label}
      </button>
    ))}
    <button className="btn btn-secondary btn-sm" style={{ marginLeft:4 }} onClick={onRefresh} disabled={loading}>
      {loading ? <span className="spinner" style={{ width:12,height:12 }} /> : '↻'}
    </button>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   ANA BİLEŞEN
   ═══════════════════════════════════════════════════════════════════════════ */
export default function Reports() {
  const { user } = useAuth();
  const isStore  = user?.dashboard_mode === 'store_group';
  const MODULES  = isStore ? STORE_MODULES : MALL_MODULES;
  const [searchParams] = useSearchParams();

  /* ── Global cihaz seçimi ── */
  const [devices,    setDevices]    = useState([]);
  const [selDevice,  setSelDevice]  = useState('');
  const [selDevices, setSelDevices] = useState([]);
  const [multiMode,  setMultiMode]  = useState(false);

  useEffect(() => {
    api.get('/reports/devices-summary').then(r => setDevices(r.data.data || [])).catch(() => {});
  }, []);

  const buildDP = useCallback(() => {
    if (multiMode && selDevices.length) return { device_ids: selDevices.join(',') };
    if (!multiMode && selDevice) return { device_id: selDevice };
    return {};
  }, [multiMode, selDevice, selDevices]);

  const toggleDev = id =>
    setSelDevices(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  /* ── Aktif modül — ?module= query param varsa ona göre aç ── */
  const initModule = (() => {
    const qm = searchParams.get('module');
    if (qm && MODULES.some(m => m.id === qm)) return qm;
    return MODULES[0].id;
  })();
  const [activeModule, setActiveModule] = useState(initModule);

  /* ── Modül değişince (cihaz değişince yeniden yükleme flag) ── */
  const loadTrigger = JSON.stringify({ selDevice, selDevices, multiMode });

  /* ════ PERF ════════════════════════════════════════════════════════════ */
  const [perfData, setPerfData] = useState(null);
  const [perfL,    setPerfL]    = useState(false);
  const loadPerf = useCallback(async () => {
    setPerfL(true);
    try {
      const { data: r } = await api.get('/reports/devices-summary');
      setPerfData(r.data || []);
    } catch (e) { console.error(e); }
    finally { setPerfL(false); }
  }, []);
  useEffect(() => { if (activeModule === 'perf') loadPerf(); }, [activeModule, loadTrigger]); // eslint-disable-line

  /* ════ CMPDEV ══════════════════════════════════════════════════════════ */
  const [cdA,      setCdA]      = useState('');
  const [cdB,      setCdB]      = useState('');
  const [cdStart,  setCdStart]  = useState(daysAgo(29));
  const [cdEnd,    setCdEnd]    = useState(todayStr());
  const [cdHourOn, setCdHourOn] = useState(false);
  const [cdSh,     setCdSh]     = useState(9);
  const [cdEh,     setCdEh]     = useState(18);
  const [cdResult, setCdResult] = useState(null);
  const [cdL,      setCdL]      = useState(false);
  const loadCd = useCallback(async () => {
    if (!cdA || !cdB) return;
    setCdL(true);
    try {
      const p = { device_a:cdA, device_b:cdB, start:cdStart, end:cdEnd };
      if (cdHourOn) { p.start_hour = cdSh; p.end_hour = cdEh; }
      const { data: r } = await api.get('/reports/compare-devices', { params: p });
      setCdResult(r.data);
    } catch (e) { console.error(e); }
    finally { setCdL(false); }
  }, [cdA, cdB, cdStart, cdEnd, cdHourOn, cdSh, cdEh]);

  /* ════ TREND ═══════════════════════════════════════════════════════════ */
  const [trendTab,  setTrendTab]  = useState('daily');
  const [trendData, setTrendData] = useState([]);
  const [trendL,    setTrendL]    = useState(false);
  const loadTrend = useCallback(async () => {
    setTrendL(true);
    const cfg = TREND_CFGS.find(t => t.key === trendTab);
    try {
      const { data: r } = await api.get(cfg.ep, { params: { ...buildDP(), ...cfg.p } });
      let rows = r.data || [];
      if (trendTab==='daily')   rows = rows.map(x => ({ ...x, label: fmtS(x.date),  total_in: Number(x.total_in) }));
      if (trendTab==='weekly')  rows = rows.map(x => ({ ...x, label: `Hf${x.wk}`,   total_in: Number(x.total_in) }));
      if (trendTab==='monthly') rows = rows.map(x => ({ ...x, label: x.period,       total_in: Number(x.total_in) }));
      if (trendTab==='yearly')  rows = rows.map(x => ({ ...x, label: String(x.yr),   total_in: Number(x.total_in) }));
      setTrendData(rows);
    } catch (e) { console.error(e); }
    finally { setTrendL(false); }
  }, [trendTab, buildDP]); // eslint-disable-line
  useEffect(() => { if (activeModule === 'trend') loadTrend(); }, [activeModule, trendTab, loadTrigger]); // eslint-disable-line

  /* ════ RANGE ═══════════════════════════════════════════════════════════ */
  const [rngStart, setRngStart] = useState(daysAgo(29));
  const [rngEnd,   setRngEnd]   = useState(todayStr());
  const [rngData,  setRngData]  = useState(null);
  const [rngL,     setRngL]     = useState(false);
  const loadRng = useCallback(async () => {
    if (!rngStart || !rngEnd) return;
    setRngL(true);
    try {
      const p = { ...buildDP(), start:rngStart, end:rngEnd };
      const [a, b] = await Promise.all([
        api.get('/reports/range',         { params: p }),
        api.get('/reports/range/summary', { params: p }),
      ]);
      setRngData({
        rows: (a.data.data || []).map(r => ({ ...r, date:safeDate(r.date), total_in:Number(r.total_in), total_out:Number(r.total_out) })),
        summ: b.data.data,
      });
    } catch (e) { console.error(e); }
    finally { setRngL(false); }
  }, [rngStart, rngEnd, buildDP]); // eslint-disable-line

  /* ════ HOURRANGE ═══════════════════════════════════════════════════════ */
  const [hrStart, setHrStart] = useState(daysAgo(29));
  const [hrEnd,   setHrEnd]   = useState(todayStr());
  const [hrSh,    setHrSh]    = useState(9);
  const [hrEh,    setHrEh]    = useState(18);
  const [hrData,  setHrData]  = useState(null);
  const [hrL,     setHrL]     = useState(false);
  const loadHr = useCallback(async () => {
    if (!hrStart || !hrEnd) return;
    setHrL(true);
    try {
      const p = { ...buildDP(), start:hrStart, end:hrEnd, start_hour:hrSh, end_hour:hrEh };
      const [a, b] = await Promise.all([
        api.get('/reports/range',         { params: p }),
        api.get('/reports/range/summary', { params: p }),
      ]);
      setHrData({
        rows: (a.data.data || []).map(r => ({ ...r, date:safeDate(r.date), total_in:Number(r.total_in) })),
        summ: b.data.data, sh:hrSh, eh:hrEh,
      });
    } catch (e) { console.error(e); }
    finally { setHrL(false); }
  }, [hrStart, hrEnd, hrSh, hrEh, buildDP]); // eslint-disable-line

  /* ════ CMPDATE ═════════════════════════════════════════════════════════ */
  const [cmpAStart, setCmpAStart] = useState(daysAgo(13));
  const [cmpAEnd,   setCmpAEnd]   = useState(daysAgo(7));
  const [cmpBStart, setCmpBStart] = useState(daysAgo(6));
  const [cmpBEnd,   setCmpBEnd]   = useState(todayStr());
  const [cmpHourOn, setCmpHourOn] = useState(false);
  const [cmpSh,     setCmpSh]     = useState(9);
  const [cmpEh,     setCmpEh]     = useState(18);
  const [cmpResult, setCmpResult] = useState(null);
  const [cmpL,      setCmpL]      = useState(false);
  const loadCmp = useCallback(async () => {
    setCmpL(true);
    try {
      const p = { ...buildDP(), a_start:cmpAStart, a_end:cmpAEnd, b_start:cmpBStart, b_end:cmpBEnd };
      if (cmpHourOn) { p.start_hour = cmpSh; p.end_hour = cmpEh; }
      const { data: r } = await api.get('/reports/compare', { params: p });
      setCmpResult(r.data);
    } catch (e) { console.error(e); }
    finally { setCmpL(false); }
  }, [cmpAStart, cmpAEnd, cmpBStart, cmpBEnd, cmpHourOn, cmpSh, cmpEh, buildDP]); // eslint-disable-line

  /* ════ TOPDAYS ═════════════════════════════════════════════════════════ */
  const [topStart, setTopStart] = useState(daysAgo(29));
  const [topEnd,   setTopEnd]   = useState(todayStr());
  const [topData,  setTopData]  = useState(null);
  const [topL,     setTopL]     = useState(false);
  const loadTop = useCallback(async () => {
    if (!topStart || !topEnd) return;
    setTopL(true);
    try {
      const { data: r } = await api.get('/reports/range', { params: { ...buildDP(), start:topStart, end:topEnd } });
      const rows = (r.data || [])
        .map(x => ({ date:safeDate(x.date), total_in:Number(x.total_in) }))
        .sort((a, b) => b.total_in - a.total_in)
        .slice(0, 10)
        .map((x, i) => ({ ...x, rank:i+1 }));
      setTopData(rows);
    } catch (e) { console.error(e); }
    finally { setTopL(false); }
  }, [topStart, topEnd, buildDP]); // eslint-disable-line

  /* ════ HOURS ═══════════════════════════════════════════════════════════ */
  const [hourDays, setHourDays] = useState(30);
  const [hourData, setHourData] = useState(null);
  const [hourL,    setHourL]    = useState(false);
  const loadHours = useCallback(async () => {
    setHourL(true);
    try {
      const { data: r } = await api.get('/reports/hour-analysis', { params: { ...buildDP(), days:hourDays } });
      setHourData(r.data);
    } catch (e) { console.error(e); }
    finally { setHourL(false); }
  }, [hourDays, buildDP]); // eslint-disable-line
  useEffect(() => { if (activeModule==='hours') loadHours(); }, [activeModule, hourDays, loadTrigger]); // eslint-disable-line

  /* ════ HEATMAP ═════════════════════════════════════════════════════════ */
  const [hmDays, setHmDays] = useState(14);
  const [hmData, setHmData] = useState([]);
  const [hmL,    setHmL]    = useState(false);
  const loadHm = useCallback(async () => {
    setHmL(true);
    try {
      const { data: r } = await api.get('/reports/heatmap', { params: { ...buildDP(), days:hmDays } });
      setHmData(r.data || []);
    } catch (e) { console.error(e); }
    finally { setHmL(false); }
  }, [hmDays, buildDP]); // eslint-disable-line
  useEffect(() => { if (activeModule==='heatmap') loadHm(); }, [activeModule, hmDays, loadTrigger]); // eslint-disable-line

  /* ── Başlık ve alt başlık ── */
  const activeLabel = MODULES.find(m => m.id === activeModule)?.label ?? '';
  const deviceLabel = isStore ? 'Şube' : 'Kapı/Cihaz';
  const devName = !multiMode
    ? (selDevice ? devices.find(d => String(d.id) === selDevice)?.name : `Tüm ${isStore ? 'şubeler' : 'cihazlar'}`)
    : `${selDevices.length} seçili`;

  /* ═══════════════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════════════ */
  return (
    <div style={{ display:'flex', gap:0, height:'100%', minHeight:700 }}>

      {/* ── Sol sidebar ── */}
      <div style={{
        width:220, flexShrink:0,
        background:'var(--surface)',
        borderRight:'1px solid var(--border)',
        display:'flex', flexDirection:'column',
        borderRadius:'var(--r) 0 0 var(--r)',
        overflow:'hidden',
      }}>
        <div style={{ padding:'14px 16px 8px', fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'.07em', color:'var(--text3)' }}>
          Rapor Modülleri
        </div>

        <div style={{ flex:1, overflowY:'auto' }}>
          {MODULES.slice(0, -1).map(m => (
            <button
              key={m.id}
              onClick={() => setActiveModule(m.id)}
              style={{
                width:'100%', display:'flex', alignItems:'center', gap:10,
                padding:'9px 16px', border:'none', textAlign:'left', cursor:'pointer', fontSize:13,
                background: activeModule===m.id ? 'var(--accent-dim)' : 'transparent',
                color:      activeModule===m.id ? 'var(--accent)'     : 'var(--text2)',
                fontWeight: activeModule===m.id ? 600 : 400,
                borderLeft: activeModule===m.id ? '2px solid var(--accent)' : '2px solid transparent',
              }}
            >
              <i className={`ti ${m.icon}`} style={{ fontSize:15 }} aria-hidden="true" />
              {m.label}
            </button>
          ))}

          <div style={{ height:1, background:'var(--border)', margin:'8px 0' }} />

          <button
            onClick={() => setActiveModule('export')}
            style={{
              width:'100%', display:'flex', alignItems:'center', gap:10,
              padding:'9px 16px', border:'none', textAlign:'left', cursor:'pointer', fontSize:13,
              background: activeModule==='export' ? 'var(--accent-dim)' : 'transparent',
              color:      activeModule==='export' ? 'var(--accent)'     : 'var(--text2)',
              fontWeight: activeModule==='export' ? 600 : 400,
              borderLeft: activeModule==='export' ? '2px solid var(--accent)' : '2px solid transparent',
            }}
          >
            <i className="ti ti-download" style={{ fontSize:15 }} aria-hidden="true" />
            Dışa Aktar
          </button>
        </div>
      </div>

      {/* ── Ana içerik ── */}
      <div style={{ flex:1, minWidth:0, padding:'20px 24px', overflowY:'auto' }}>

        {/* Başlık + cihaz filtresi */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:18, gap:12, flexWrap:'wrap' }}>
          <div>
            <h1 style={{ fontSize:18, fontWeight:500, marginBottom:3 }}>{activeLabel}</h1>
            <p style={{ fontSize:12, color:'var(--text3)' }}>{devName}</p>
          </div>

          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            <button className={`btn btn-sm ${!multiMode?'btn-primary':'btn-secondary'}`} onClick={() => setMultiMode(false)}>Tekli</button>
            <button className={`btn btn-sm ${multiMode ?'btn-primary':'btn-secondary'}`} onClick={() => setMultiMode(true)}>Çoklu</button>

            {!multiMode ? (
              <select value={selDevice} onChange={e => setSelDevice(e.target.value)} style={{ minWidth:180 }}>
                <option value="">Tüm {isStore ? 'Şubeler' : 'Cihazlar'}</option>
                {devices.map(d => <option key={d.id} value={d.id}>{d.status==='online'?'● ':'○ '}{d.name}</option>)}
              </select>
            ) : (
              <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                {devices.map(d => (
                  <button key={d.id} onClick={() => toggleDev(d.id)}
                    className={`btn btn-sm ${selDevices.includes(d.id)?'btn-primary':'btn-secondary'}`}>
                    {d.status==='online'?'● ':'○ '}{d.name}
                  </button>
                ))}
                {selDevices.length > 0 && <button className="btn btn-secondary btn-sm" onClick={() => setSelDevices([])}>✕</button>}
              </div>
            )}
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════
            MODÜL İÇERİKLERİ
            ══════════════════════════════════════════════════════════════ */}

        {/* PERF */}
        {activeModule === 'perf' && (
          <PerfPanel data={perfData} loading={perfL} onRefresh={loadPerf} isStore={isStore} selDevice={selDevice} selDevices={selDevices} multiMode={multiMode} />
        )}

        {/* CMPDEV */}
        {activeModule === 'cmpdev' && (
          <CmpDevPanel
            devices={devices} isStore={isStore}
            devA={cdA} devB={cdB} onDevA={setCdA} onDevB={setCdB}
            start={cdStart} end={cdEnd} onStart={setCdStart} onEnd={setCdEnd}
            hourOn={cdHourOn} onHourOn={setCdHourOn} sh={cdSh} eh={cdEh} onSh={setCdSh} onEh={setCdEh}
            result={cdResult} loading={cdL} onApply={loadCd}
          />
        )}

        {/* TREND */}
        {activeModule === 'trend' && (
          <TrendPanel tab={trendTab} onTab={setTrendTab} data={trendData} loading={trendL} onRefresh={loadTrend} />
        )}

        {/* RANGE */}
        {activeModule === 'range' && (
          <div>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center', marginBottom:16,
              padding:'14px 18px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r)' }}>
              <DatePair start={rngStart} end={rngEnd} onStart={setRngStart} onEnd={setRngEnd} />
              <ApplyBtn onClick={loadRng} loading={rngL} disabled={!rngStart||!rngEnd} />
              <QuickDates onSelect={(s,e) => { setRngStart(s); setRngEnd(e); }} />
            </div>
            {!rngData && !rngL && <Mty msg="Tarih seçip Uygula'ya basın" />}
            {rngL && <Spn />}
            {rngData && !rngL && <RangePanel data={rngData} />}
          </div>
        )}

        {/* HOURRANGE */}
        {activeModule === 'hourrange' && (
          <div>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center', marginBottom:16,
              padding:'14px 18px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r)' }}>
              <DatePair start={hrStart} end={hrEnd} onStart={setHrStart} onEnd={setHrEnd} />
              <HourPair sh={hrSh} eh={hrEh} onSh={setHrSh} onEh={setHrEh} />
              <ApplyBtn onClick={loadHr} loading={hrL} disabled={!hrStart||!hrEnd} />
            </div>
            {!hrData && !hrL && <Mty msg="Tarih ve saat seçip Uygula'ya basın" />}
            {hrL && <Spn />}
            {hrData && !hrL && <HourRangePanel data={hrData} />}
          </div>
        )}

        {/* CMPDATE */}
        {activeModule === 'cmpdate' && (
          <div>
            <div style={{ padding:'16px 18px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r)', marginBottom:16 }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:14, marginBottom:12 }}>
                <div>
                  <div style={{ fontSize:11, fontWeight:600, color:'var(--accent)', marginBottom:6 }}>A Dönemi</div>
                  <DatePair start={cmpAStart} end={cmpAEnd} onStart={setCmpAStart} onEnd={setCmpAEnd} />
                </div>
                <div>
                  <div style={{ fontSize:11, fontWeight:600, color:'var(--purple)', marginBottom:6 }}>B Dönemi</div>
                  <DatePair start={cmpBStart} end={cmpBEnd} onStart={setCmpBStart} onEnd={setCmpBEnd} />
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, cursor:'pointer', fontWeight:600 }}>
                  <input type="checkbox" checked={cmpHourOn} onChange={e => setCmpHourOn(e.target.checked)} />
                  Saat filtresi
                </label>
                {cmpHourOn && <HourPair sh={cmpSh} eh={cmpEh} onSh={setCmpSh} onEh={setCmpEh} />}
                <ApplyBtn onClick={loadCmp} loading={cmpL} />
              </div>
            </div>
            {!cmpResult && !cmpL && <Mty msg="A ve B dönemlerini seçip Uygula'ya basın" />}
            {cmpL && <Spn />}
            {cmpResult && !cmpL && <CmpDatePanel data={cmpResult} aLabel={cmpAStart} bLabel={cmpBStart} />}
          </div>
        )}

        {/* TOPDAYS */}
        {activeModule === 'topdays' && (
          <div>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center', marginBottom:16,
              padding:'14px 18px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r)' }}>
              <DatePair start={topStart} end={topEnd} onStart={setTopStart} onEnd={setTopEnd} />
              <ApplyBtn onClick={loadTop} loading={topL} disabled={!topStart||!topEnd} />
              <QuickDates onSelect={(s,e) => { setTopStart(s); setTopEnd(e); }} />
            </div>
            {!topData && !topL && <Mty msg="Tarih seçip Uygula'ya basın" />}
            {topL && <Spn />}
            {topData && !topL && <TopDaysPanel data={topData} />}
          </div>
        )}

        {/* HOURS */}
        {activeModule === 'hours' && (
          <div>
            <div style={{ display:'flex', gap:7, alignItems:'center', marginBottom:16, flexWrap:'wrap',
              padding:'12px 18px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r)' }}>
              <span style={{ fontSize:12, fontWeight:600, color:'var(--text2)' }}>Dönem:</span>
              {[14,30,60,90].map(d => (
                <button key={d} className={`btn btn-sm ${hourDays===d?'btn-primary':'btn-secondary'}`}
                  onClick={() => setHourDays(d)}>Son {d} gün</button>
              ))}
              <button className="btn btn-secondary btn-sm" onClick={loadHours} disabled={hourL}>
                {hourL ? <span className="spinner" style={{ width:12,height:12 }} /> : '↻ Yenile'}
              </button>
            </div>
            {hourL && <Spn />}
            {hourData && !hourL && <HoursPanel data={hourData} />}
            {!hourData && !hourL && <Mty msg="Dönem seçip Yenile'ye basın" />}
          </div>
        )}

        {/* HEATMAP */}
        {activeModule === 'heatmap' && (
          <div>
            <div style={{ display:'flex', gap:7, alignItems:'center', marginBottom:16, flexWrap:'wrap',
              padding:'12px 18px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r)' }}>
              <span style={{ fontSize:12, fontWeight:600, color:'var(--text2)' }}>Dönem:</span>
              {[14,30,60].map(d => (
                <button key={d} className={`btn btn-sm ${hmDays===d?'btn-primary':'btn-secondary'}`}
                  onClick={() => setHmDays(d)}>Son {d} gün</button>
              ))}
              <button className="btn btn-secondary btn-sm" onClick={loadHm} disabled={hmL}>
                {hmL ? <span className="spinner" style={{ width:12,height:12 }} /> : '↻ Yenile'}
              </button>
            </div>
            {hmL && <Spn />}
            {!hmL && <div className="card" style={{ padding:'18px' }}><HeatmapChart data={hmData} /></div>}
          </div>
        )}

        {/* EXPORT */}
        {activeModule === 'export' && (
          <ExportPanel buildDP={buildDP} devices={devices} selDevice={selDevice} selDevices={selDevices} multiMode={multiMode} isStore={isStore} />
        )}

      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   PANEL BİLEŞENLERİ
   ═══════════════════════════════════════════════════════════════════════════ */

function PerfPanel({ data, loading, onRefresh, isStore, selDevice, selDevices, multiMode }) {
  if (loading) return <Spn />;
  if (!data)   return <Mty msg="Yükleniyor..." />;
  if (data.length === 0) return <Mty msg={isStore ? 'Şube bulunamadı' : 'Cihaz bulunamadı'} />;

  const filteredData = multiMode && selDevices?.length
    ? data.filter(d => selDevices.map(String).includes(String(d.id)))
    : (!multiMode && selDevice ? data.filter(d => String(d.id) === String(selDevice)) : data);

  const sorted = [...filteredData].sort((a, b) => Number(b.today_in) - Number(a.today_in));
  const topId  = sorted[0]?.id;
  const total  = sorted.reduce((s, d) => s + Number(d.today_in || 0), 0);
  const online = sorted.filter(d => d.status === 'online').length;
  const isSingle = !multiMode && !!selDevice;

  return (
    <>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <span style={{ fontSize:12, color:'var(--text3)' }}>Bugünkü anlık veriler</span>
        <button className="btn btn-secondary btn-sm" onClick={onRefresh}>
          <i className="ti ti-refresh" style={{ fontSize:13, marginRight:4 }} aria-hidden="true" />Yenile
        </button>
      </div>

      <KRow cols={4}>
        <KCard label={`Toplam bugün`} value={fmt(total)} />
        <KCard label={isSingle ? (isStore ? 'Şube durumu' : 'Cihaz durumu') : (isStore ? 'Aktif şube' : 'Online cihaz')} value={isSingle ? (sorted[0]?.status === 'online' ? 'Aktif' : 'Pasif') : `${online} / ${sorted.length}`} color="var(--green)" />
        <KCard label={isSingle ? (isStore ? 'Seçili şube' : 'Seçili cihaz') : 'En yoğun'} value={sorted[0]?.name} sub={fmt(sorted[0]?.today_in)+' giriş'} color="var(--green)" accent="var(--accent)" />
        <KCard label={isSingle ? 'Bugünkü giriş' : 'Şube başına ort.'} value={isSingle ? fmt(sorted[0]?.today_in) : (sorted.length ? fmt(Math.round(total/sorted.length)) : '—')} />
      </KRow>

      {/* Progress sıralaması */}
      <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:20 }}>
        {sorted.map((d, i) => {
          const pct = total > 0 ? (Number(d.today_in) / sorted[0]?.today_in) * 100 : 0;
          return (
            <div key={d.id} style={{
              display:'flex', alignItems:'center', gap:12, padding:'10px 16px',
              background: d.id===topId ? 'var(--green-dim,rgba(34,197,94,.06))' : 'var(--surface)',
              border:`1px solid ${d.id===topId?'rgba(34,197,94,.25)':'var(--border)'}`,
              borderRadius:'var(--r)',
            }}>
              <span style={{ width:8, height:8, borderRadius:'50%', flexShrink:0, background: d.status==='online'?'var(--green)':'var(--text3)', boxShadow: d.status==='online'?'0 0 6px var(--green)':'none' }} />
              <span style={{ width:130, fontSize:13, fontWeight: d.id===topId?600:400, color: d.status==='online'?'var(--text)':'var(--text3)', flexShrink:0 }}>{d.name}</span>
              <div style={{ flex:1, height:5, background:'var(--border)', borderRadius:3 }}>
                {d.status==='online' && <div style={{ height:5, width:`${pct}%`, background: d.id===topId?'var(--green)':'var(--accent)', borderRadius:3, transition:'width .4s' }} />}
              </div>
              <span style={{ fontSize:14, fontWeight:600, color: d.id===topId?'var(--green)':'var(--text)', minWidth:48, textAlign:'right' }}>
                {d.status==='online' ? fmt(d.today_in) : '—'}
              </span>
              {d.status!=='online' && <span style={{ fontSize:10, padding:'2px 7px', borderRadius:10, background:'var(--bg3)', color:'var(--text3)' }}>{isStore?'Pasif':'Offline'}</span>}
              {d.id===topId && <span style={{ fontSize:10, padding:'2px 7px', borderRadius:10, background:'var(--green-dim,rgba(34,197,94,.12))', color:'var(--green)', fontWeight:600 }}>En Yoğun</span>}
            </div>
          );
        })}
      </div>

      {/* Alt: Yatay bar grafik */}
      <div className="card" style={{ padding:'16px 18px' }}>
        <h3 style={{ fontSize:14, marginBottom:14 }}>Ziyaretçi dağılımı</h3>
        <ResponsiveContainer width="100%" height={Math.max(160, sorted.length * 40)}>
          <BarChart data={sorted} layout="vertical" margin={{ top:4, right:20, bottom:4, left:4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
            <XAxis type="number" tick={{ fill:'var(--text3)', fontSize:10 }} />
            <YAxis type="category" dataKey="name" tick={{ fill:'var(--text2)', fontSize:11 }} width={115} />
            <Tooltip content={<Tip />} />
            <Bar dataKey="today_in" name="Bugün Giriş" radius={[0,4,4,0]}>
              {sorted.map((d, i) => <Cell key={i} fill={d.id===topId?'var(--green)':'var(--accent)'} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}

function CmpDevPanel({ devices, isStore, devA, devB, onDevA, onDevB, start, end, onStart, onEnd, hourOn, onHourOn, sh, eh, onSh, onEh, result, loading, onApply }) {
  const lbl = isStore ? 'Şube' : 'Kapı';
  return (
    <>
      <div style={{ padding:'16px 18px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r)', marginBottom:16 }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:12 }}>
          <div>
            <div style={{ fontSize:11, fontWeight:600, color:'var(--accent)', marginBottom:6 }}>{lbl} A</div>
            <select value={devA} onChange={e => onDevA(e.target.value)} style={{ width:'100%' }}>
              <option value="">{lbl} seçin...</option>
              {devices.filter(d => String(d.id)!==String(devB)).map(d => (
                <option key={d.id} value={d.id}>{d.status==='online'?'● ':'○ '}{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize:11, fontWeight:600, color:'var(--purple)', marginBottom:6 }}>{lbl} B</div>
            <select value={devB} onChange={e => onDevB(e.target.value)} style={{ width:'100%' }}>
              <option value="">{lbl} seçin...</option>
              {devices.filter(d => String(d.id)!==String(devA)).map(d => (
                <option key={d.id} value={d.id}>{d.status==='online'?'● ':'○ '}{d.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center', marginBottom:10 }}>
          <DatePair start={start} end={end} onStart={onStart} onEnd={onEnd} />
          <QuickDates onSelect={(s,e) => { onStart(s); onEnd(e); }} />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, cursor:'pointer', fontWeight:600 }}>
            <input type="checkbox" checked={hourOn} onChange={e => onHourOn(e.target.checked)} />
            Saat filtresi
          </label>
          {hourOn && <HourPair sh={sh} eh={eh} onSh={onSh} onEh={onEh} />}
          <ApplyBtn onClick={onApply} loading={loading} disabled={!devA||!devB} />
        </div>
      </div>

      {!devA && !devB && !result && <Mty msg={`İki ${lbl.toLowerCase()} seçip Karşılaştır'a basın`} />}
      {loading && <Spn />}
      {result && !loading && (
        <>
          <KRow cols={4}>
            <KCard label={`${result.a.device_name} (A)`}  value={fmt(result.a.total)} color="var(--accent)" />
            <KCard label={`${result.b.device_name} (B)`}  value={fmt(result.b.total)} color="var(--purple)" />
            <KCard label="Fark (A−B)" value={(result.totals.diff>=0?'+':'')+fmt(result.totals.diff)}
              color={result.totals.diff>=0?'var(--green)':'var(--red)'} />
            <KCard label="% Değişim"
              value={result.totals.diff_pct!==null?(result.totals.diff_pct>=0?'+':'')+result.totals.diff_pct+'%':'—'}
              color={result.totals.diff_pct>=0?'var(--green)':'var(--red)'} />
          </KRow>
          <div className="card" style={{ padding:'16px 18px', marginBottom:14 }}>
            <h3 style={{ fontSize:14, marginBottom:14 }}>Günlük karşılaştırma</h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={result.rows.map(r=>({...r, label:fmtS(r.date)}))} margin={{top:5,right:16,bottom:5,left:-10}}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="label" tick={{fill:'var(--text3)',fontSize:9}} />
                <YAxis tick={{fill:'var(--text3)',fontSize:10}} />
                <Tooltip content={<Tip />} />
                <Legend />
                <Line type="monotone" dataKey="a_total_in" stroke="var(--accent)"  strokeWidth={2.5} dot={false} name={`${result.a.device_name} (A)`} connectNulls />
                <Line type="monotone" dataKey="b_total_in" stroke="var(--purple)"  strokeWidth={2.5} dot={false} name={`${result.b.device_name} (B)`} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="card" style={{ padding:'16px 18px' }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Tarih</th><th style={{color:'var(--accent)'}}>{result.a.device_name} (A)</th><th style={{color:'var(--purple)'}}>{result.b.device_name} (B)</th><th>Fark</th></tr>
                </thead>
                <tbody>
                  {result.rows.map((r,i) => (
                    <tr key={i}>
                      <td>{fmtL(r.date)}</td>
                      <td style={{color:'var(--accent)',fontWeight:500}}>{fmt(r.a_total_in)}</td>
                      <td style={{color:'var(--purple)',fontWeight:500}}>{fmt(r.b_total_in)}</td>
                      <td style={{color:r.diff>0?'var(--green)':r.diff<0?'var(--red)':'var(--text3)',fontWeight:600}}>
                        {(r.diff>=0?'+':'')+fmt(r.diff)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function TrendPanel({ tab, onTab, data, loading, onRefresh }) {
  const total = data.reduce((s,r) => s+Number(r.total_in||0), 0);
  const avg   = data.length ? Math.round(total/data.length) : 0;
  const max   = data.length ? Math.max(...data.map(r=>Number(r.total_in||0))) : 0;
  const maxL  = data.find(r=>Number(r.total_in)===max)?.label||'';
  return (
    <>
      <TrendSubtabs tab={tab} onTab={onTab} loading={loading} onRefresh={onRefresh} />
      {loading ? <Spn /> : (
        <>
          <KRow cols={4}>
            <KCard label="Toplam"    value={fmt(total)} />
            <KCard label="Ortalama"  value={fmt(avg)}   color="var(--purple)" />
            <KCard label="En yüksek" value={fmt(max)}   color="var(--green)" sub={maxL} />
            <KCard label="Kayıt"     value={data.length} />
          </KRow>
          <div className="card" style={{ padding:'16px 18px', marginBottom:14 }}>
            {data.length===0 ? <Mty /> : (
              <ResponsiveContainer width="100%" height={280}>
                {(tab==='daily'||tab==='weekly') ? (
                  <LineChart data={data} margin={{top:5,right:16,bottom:5,left:-10}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="label" tick={{fill:'var(--text3)',fontSize:10}} />
                    <YAxis tick={{fill:'var(--text3)',fontSize:10}} />
                    <Tooltip content={<Tip />} />
                    <Line type="monotone" dataKey="total_in" stroke="var(--accent)" strokeWidth={2.5} dot={false} name="Giriş" />
                  </LineChart>
                ) : (
                  <BarChart data={data} margin={{top:5,right:16,bottom:5,left:-10}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="label" tick={{fill:'var(--text3)',fontSize:11}} />
                    <YAxis tick={{fill:'var(--text3)',fontSize:10}} />
                    <Tooltip content={<Tip />} />
                    <Bar dataKey="total_in" fill="var(--accent)" radius={[4,4,0,0]} name="Giriş" />
                  </BarChart>
                )}
              </ResponsiveContainer>
            )}
          </div>
          <div className="card" style={{ padding:'16px 18px' }}>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Dönem</th><th>Giriş</th><th>Çıkış</th></tr></thead>
                <tbody>
                  {data.slice().reverse().slice(0,60).map((row,i) => (
                    <tr key={i}>
                      <td>{row.label}</td>
                      <td><strong>{fmt(row.total_in)}</strong></td>
                      <td style={{color:'var(--text2)'}}>{fmt(row.total_out)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function RangePanel({ data:{ rows, summ } }) {
  return (
    <>
      <KRow cols={4}>
        <KCard label="Toplam giriş"  value={fmt(summ?.total_in)}    color="var(--accent)" />
        <KCard label="Günlük ort."   value={fmt(summ?.avg_per_day)} color="var(--purple)" />
        <KCard label="En yoğun gün"  value={fmtL(summ?.peak_date)}  color="var(--green)"
          sub={rows.find(r=>r.date===summ?.peak_date)?.total_in?.toLocaleString('tr-TR')+' giriş'} />
        <KCard label="Gün sayısı"    value={summ?.days_count??rows.length} />
      </KRow>
      {rows.length===0 ? <Mty /> : (
        <div className="card" style={{ padding:'16px 18px', marginBottom:14 }}>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={rows} margin={{top:5,right:16,bottom:5,left:-10}}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" tick={{fill:'var(--text3)',fontSize:9}} tickFormatter={fmtS} />
              <YAxis tick={{fill:'var(--text3)',fontSize:10}} />
              <Tooltip content={<Tip />} labelFormatter={fmtL} />
              <Line type="monotone" dataKey="total_in" stroke="var(--accent)" strokeWidth={2.5} dot={false} name="Giriş" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="card" style={{ padding:'16px 18px' }}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Tarih</th><th>Giriş</th><th>Çıkış</th></tr></thead>
            <tbody>
              {rows.slice().reverse().slice(0,60).map((r,i)=>(
                <tr key={i}>
                  <td>{fmtL(r.date)}</td>
                  <td><strong>{fmt(r.total_in)}</strong></td>
                  <td style={{color:'var(--text2)'}}>{fmt(r.total_out)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function HourRangePanel({ data:{ rows, summ, sh, eh } }) {
  const lbl = `${String(sh).padStart(2,'0')}:00–${String(eh).padStart(2,'0')}:00`;
  return (
    <>
      <div style={{ fontSize:12, color:'var(--accent)', fontWeight:600, marginBottom:14, padding:'8px 14px', background:'var(--accent-dim)', borderRadius:'var(--r)' }}>
        {lbl} saat aralığı raporu
      </div>
      <KRow cols={4}>
        <KCard label="Toplam giriş" value={fmt(summ?.total_in)} color="var(--accent)" />
        <KCard label="Günlük ort."  value={fmt(summ?.avg_per_day)} color="var(--purple)" />
        <KCard label="En yoğun gün" value={fmtL(summ?.peak_date)} color="var(--green)" />
        <KCard label="Gün sayısı"   value={rows.length} />
      </KRow>
      {rows.length===0 ? <Mty /> : (
        <div className="card" style={{ padding:'16px 18px', marginBottom:14 }}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={rows} margin={{top:5,right:16,bottom:5,left:-10}}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" tick={{fill:'var(--text3)',fontSize:9}} tickFormatter={fmtS} />
              <YAxis tick={{fill:'var(--text3)',fontSize:10}} />
              <Tooltip content={<Tip />} labelFormatter={fmtL} />
              <Bar dataKey="total_in" fill="var(--accent)" radius={[3,3,0,0]} name={`Giriş (${lbl})`} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="card" style={{ padding:'16px 18px' }}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Tarih</th><th>Giriş ({lbl})</th></tr></thead>
            <tbody>
              {rows.slice().reverse().map((r,i)=>(
                <tr key={i}><td>{fmtL(r.date)}</td><td><strong>{fmt(r.total_in)}</strong></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function CmpDatePanel({ data, aLabel, bLabel }) {
  const { a, b, totals } = data;
  const merged = Array.from({length:Math.max(a.length,b.length)},(_,i)=>({
    label:`Gün ${i+1}`, a:a[i]?.total_in??null, b:b[i]?.total_in??null,
  }));
  return (
    <>
      <KRow cols={4}>
        <KCard label="A toplam"   value={fmt(totals.a_total)} color="var(--accent)" />
        <KCard label="B toplam"   value={fmt(totals.b_total)} color="var(--purple)" />
        <KCard label="Fark (B−A)" value={(totals.diff>=0?'+':'')+fmt(totals.diff)}
          color={totals.diff>=0?'var(--green)':'var(--red)'} />
        <KCard label="% Değişim"
          value={totals.diff_pct!==null?(totals.diff_pct>=0?'+':'')+totals.diff_pct+'%':'—'}
          color={totals.diff_pct>=0?'var(--green)':'var(--red)'} />
      </KRow>
      <div className="card" style={{ padding:'16px 18px', marginBottom:14 }}>
        <h3 style={{ fontSize:14, marginBottom:14 }}>A vs B — gün bazlı</h3>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={merged} margin={{top:5,right:16,bottom:5,left:-10}}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="label" tick={{fill:'var(--text3)',fontSize:9}} />
            <YAxis tick={{fill:'var(--text3)',fontSize:10}} />
            <Tooltip content={<Tip />} />
            <Legend />
            <Line type="monotone" dataKey="a" stroke="var(--accent)"  strokeWidth={2.5} dot={false} name={`A (${aLabel})`} connectNulls />
            <Line type="monotone" dataKey="b" stroke="var(--purple)"  strokeWidth={2.5} dot={false} name={`B (${bLabel})`} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="card" style={{ padding:'16px 18px' }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Gün</th><th style={{color:'var(--accent)'}}>A ({aLabel})</th><th style={{color:'var(--purple)'}}>B ({bLabel})</th><th>Fark</th></tr>
            </thead>
            <tbody>
              {merged.map((row,i) => {
                const diff = (b[i]?.total_in??0)-(a[i]?.total_in??0);
                return (
                  <tr key={i}>
                    <td style={{color:'var(--text3)'}}>Gün {i+1}</td>
                    <td style={{color:'var(--accent)',fontWeight:500}}>{a[i]?fmt(a[i].total_in):'—'}</td>
                    <td style={{color:'var(--purple)',fontWeight:500}}>{b[i]?fmt(b[i].total_in):'—'}</td>
                    <td style={{color:diff>0?'var(--green)':diff<0?'var(--red)':'var(--text3)',fontWeight:600}}>
                      {a[i]&&b[i]?(diff>=0?'+':'')+fmt(diff):'—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function TopDaysPanel({ data }) {
  if (data.length===0) return <Mty />;
  return (
    <>
      <div className="card" style={{ padding:'16px 18px', marginBottom:14 }}>
        <h3 style={{ fontSize:14, marginBottom:14 }}>Top 10 — en yoğun günler</h3>
        <ResponsiveContainer width="100%" height={Math.max(180,data.length*40)}>
          <BarChart data={data} layout="vertical" margin={{top:4,right:20,bottom:4,left:8}}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
            <XAxis type="number" tick={{fill:'var(--text3)',fontSize:10}} />
            <YAxis type="category" dataKey="date" tick={{fill:'var(--text2)',fontSize:10}} width={75} tickFormatter={fmtL} />
            <Tooltip content={<Tip />} labelFormatter={fmtL} />
            <Bar dataKey="total_in" name="Giriş" radius={[0,4,4,0]}>
              {data.map((_,i) => <Cell key={i} fill={i===0?'var(--green)':'var(--accent)'} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="card" style={{ padding:'16px 18px' }}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>#</th><th>Tarih</th><th>Giriş</th></tr></thead>
            <tbody>
              {data.map((r,i)=>(
                <tr key={i}>
                  <td style={{color:i===0?'var(--green)':i===1?'var(--yellow)':'var(--text3)',fontWeight:700}}>
                    {i===0?'🥇':i===1?'🥈':i===2?'🥉':i+1}
                  </td>
                  <td>{fmtL(r.date)}</td>
                  <td><strong>{fmt(r.total_in)}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function HoursPanel({ data }) {
  const hrs    = data?.data || [];
  const sorted = [...hrs].sort((a,b) => b.avg_in-a.avg_in);
  const busy   = sorted.slice(0,5);
  const quiet  = sorted.filter(r=>r.avg_in>0).slice(-5).reverse();
  if (hrs.length===0) return <Mty />;
  return (
    <>
      <div className="card" style={{ padding:'16px 18px', marginBottom:14 }}>
        <h3 style={{ fontSize:14, marginBottom:14 }}>Saatlik ortalama dağılım</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={hrs} margin={{top:4,right:8,bottom:4,left:-20}}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="hour" tick={{fill:'var(--text3)',fontSize:9}} tickFormatter={h=>`${h}`} />
            <YAxis tick={{fill:'var(--text3)',fontSize:10}} />
            <Tooltip content={<Tip />} labelFormatter={h=>`${String(h).padStart(2,'0')}:00`} />
            <Bar dataKey="avg_in" name="Ort. Giriş" radius={[2,2,0,0]}>
              {hrs.map((r,i)=>{
                const isBusy  = busy.some(b=>b.hour===r.hour);
                const isQuiet = quiet.some(q=>q.hour===r.hour);
                return <Cell key={i} fill={isBusy?'var(--accent)':isQuiet?'var(--border2)':'var(--accent-dim)'} />;
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        <div className="card" style={{ padding:'16px 18px' }}>
          <h4 style={{ fontSize:13, color:'var(--accent)', marginBottom:12 }}>En yoğun 5 saat</h4>
          {busy.map((r,i)=>(
            <div key={i} style={{ display:'flex', justifyContent:'space-between', marginBottom:8, fontSize:13 }}>
              <span style={{ color:'var(--text2)' }}>{i+1}. {String(r.hour).padStart(2,'0')}:00</span>
              <strong style={{ color:'var(--accent)' }}>{fmt(r.avg_in)}</strong>
            </div>
          ))}
        </div>
        <div className="card" style={{ padding:'16px 18px' }}>
          <h4 style={{ fontSize:13, color:'var(--text3)', marginBottom:12 }}>En sakin 5 saat</h4>
          {quiet.map((r,i)=>(
            <div key={i} style={{ display:'flex', justifyContent:'space-between', marginBottom:8, fontSize:13 }}>
              <span style={{ color:'var(--text2)' }}>{i+1}. {String(r.hour).padStart(2,'0')}:00</span>
              <strong style={{ color:'var(--text3)' }}>{fmt(r.avg_in)}</strong>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function ExportPanel({ buildDP, devices, selDevice, selDevices, multiMode, isStore }) {
  const [expStart, setExpStart] = useState(daysAgo(29));
  const [expEnd,   setExpEnd]   = useState(todayStr());
  const devName = !multiMode
    ? (selDevice ? devices.find(d=>String(d.id)===selDevice)?.name : `Tüm ${isStore?'şubeler':'cihazlar'}`)
    : `${selDevices.length} seçili`;

  return (
    <div style={{ maxWidth:560 }}>
      <div style={{ padding:'20px 22px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r)', marginBottom:14 }}>
        <h3 style={{ fontSize:14, marginBottom:14 }}>Tarih aralığı seç</h3>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center', marginBottom:14 }}>
          <DatePair start={expStart} end={expEnd} onStart={setExpStart} onEnd={setExpEnd} />
          <QuickDates onSelect={(s,e)=>{ setExpStart(s); setExpEnd(e); }} />
        </div>
        <div style={{ fontSize:12, color:'var(--text2)', marginBottom:16 }}>
          <strong>{fmtL(expStart)}</strong> — <strong>{fmtL(expEnd)}</strong>
          <span style={{ marginLeft:10 }}>• {devName}</span>
        </div>
        <ExportButtons params={{ start:expStart, end:expEnd, ...buildDP() }} />
      </div>
    </div>
  );
}
